import React, { useState, useEffect, KeyboardEvent } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import {
    Button,
    IconWarningSign,
    Input,
    LoadingEllipses,
    GradientDot,
    IconTrqkMark,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { getWebpackEnvVar, locale, constants } from '@trqk/utils';

const { SIGN_IN_WITH_COGNITO, GET_SESSION_CURRENT_USER } = queryStrings;

const width = 400;

/**
 * A form that requires a user to sign in to refresh their token before proceeding.
 */
export const RefreshTokenSignIn = ({
    remove,
}: ModalContentProps<Record<string, never>>): JSX.Element => {
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordWasEdited, setPasswordWasEdited] = useState(false);
    const [signInErrorMessage, setSignInErrorMessage] = useState('');

    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const sessionEmail =
        currentUserQuery?.data?.session?.currentUser?.email ?? null;

    useEffect(() => {
        if (sessionEmail !== null) {
            setEmail(sessionEmail);
        }
    }, [sessionEmail]);

    const [signInWithCognito, { loading }] = useMutation(SIGN_IN_WITH_COGNITO, {
        onCompleted: data => {
            const error = data?.signInWithCognito?.error ?? null;

            if (error) {
                if (
                    error.code ===
                    constants.ERROR_CODES.AWS.NOT_AUTHORIZED_EXCEPTION
                ) {
                    setSignInErrorMessage(
                        locale.enUS.SIGN_IN.ERRORS.BAD_CREDENTIALS,
                    );
                } else {
                    setSignInErrorMessage(locale.enUS.DEFAULT_API_ERROR);
                }
            } else {
                const token = data?.signInWithCognito?.token ?? null;

                remove({
                    success: true,
                    canceled: false,
                    output: token,
                });
            }
        },
    });

    const disableFormSubmission =
        email.length < 1 || password.length < 1 || !passwordWasEdited;

    return (
        <div
            className="flex flex-col justify-center items-center bg-white rounded-lg p-10"
            style={{ width }}
        >
            <form>
                <div className="w-full">
                    <div className="flex justify-center">
                        <GradientDot gradientColor="primary" width={90}>
                            <IconTrqkMark width={40} fillColor="white" />
                        </GradientDot>
                    </div>
                    <div className="mt-10 mb-3 flex justify-center items-start">
                        <IconWarningSign fillColor="quinary-500" />
                        <div className="ml-2 mr-3">
                            <div className="font-semibold text-quinary-500 mb-2">
                                {locale.enUS.SIGN_IN.SESSION_HAS_EXPIRED}
                            </div>
                            <div className="text-sm italic">
                                {locale.enUS.SIGN_IN.SIGN_IN_TO_CONTINUE}
                            </div>
                        </div>
                    </div>
                    <Input
                        addClasses="my-2 border border-tertiary-200"
                        autoComplete="current-password"
                        inputId="refresh-token-sign-in-password"
                        icon="password"
                        type="password"
                        placeholder="Password"
                        value={password}
                        handleChange={setPassword}
                        handleBlur={(): void => {
                            setPasswordWasEdited(true);
                        }}
                        padding="large"
                        handleKeyPress={(e: KeyboardEvent<Element>): void => {
                            setPasswordWasEdited(true);

                            if (e.key === 'Enter') {
                                setSignInErrorMessage('');

                                signInWithCognito({
                                    variables: {
                                        username: email,
                                        password,
                                        clientId,
                                        userPoolId,
                                        updateUiState: true,
                                    },
                                });
                            }
                        }}
                    />
                </div>
                <div className="text-center text-sm h-6 text-quinary-500">
                    {signInErrorMessage && <>{signInErrorMessage}</>}
                </div>
                <div className="w-1/2 mt-10">
                    <Button
                        addClasses="w-full"
                        color="primary-light-bg"
                        disabled={disableFormSubmission}
                        handleClick={(): void => {
                            signInWithCognito({
                                variables: {
                                    username: email,
                                    password,
                                    clientId,
                                    userPoolId,
                                    updateUiState: true,
                                },
                            });
                        }}
                    >
                        <div className="text-center mx-3 tracking-wider">
                            {!loading && (
                                <div>{locale.enUS.SIGN_IN.BUTTONS.SIGN_IN}</div>
                            )}
                            {loading && <LoadingEllipses width={40} />}
                        </div>
                    </Button>
                </div>
            </form>
        </div>
    );
};
